/**
 * PostHogIdentifier, sets rich person properties on the PostHog profile
 * whenever the authenticated user changes.
 *
 * Fetches profile + subscription data from Supabase and passes it to
 * posthog.identify() so funnels can be segmented by plan, referral source, etc.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { identifyUser, resetUser, aliasIdentity } from '../lib/posthog';
import { setPixelAdvancedMatching } from '../lib/fbpixel';

/**
 * Window event any component can dispatch after it changes something that
 * lives on the PostHog person profile (bill count, plan, subscription status).
 *
 * ⚠️ 2026-08-08: identify() previously only re-ran when the `user` object
 * itself changed, so after a bill upload or an upgrade the PostHog person kept
 * stale `bills_used` / `plan` / `subscription_status` for the rest of the
 * session — while Intercom, which those same call sites DID update, stayed
 * current. Cohorts built on plan or bills_used were reading yesterday's truth.
 */
export const IDENTITY_REFRESH_EVENT = 'mediloop:identity-refresh';

/** Ask PostHogIdentifier to re-read profile + subscription and re-identify. */
export function refreshPostHogIdentity(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(IDENTITY_REFRESH_EVENT));
}

// Local row shape for what we read off `profiles` and `subscriptions` here.
// Declared explicitly because Supabase's PostgrestQueryBuilder only infers a
// usable result type when `.select()` receives a single string literal  
// our profile query previously used `+` string concatenation to fit under
// a line-length budget, which defeated inference and made every field
// land as `GenericStringError`. Casting through this type fixes that
// without re-introducing the long line.
type ProfileRow = {
  first_name: string | null;
  last_name: string | null;
  referral_source: string | null;
  gender: string | null;
  state: string | null;
  created_at: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  initial_referrer: string | null;
  landing_page: string | null;
  acquisition_channel: string | null;
  is_test: boolean | null;
};

type SubscriptionRow = {
  plan: string | null;
  status: string | null;
  bills_used: number | null;
  bills_limit: number | null;
};

export default function PostHogIdentifier({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();

  /* Whether we've seen an authenticated user this session. Used to only call
     posthog.reset() on a REAL logout (authenticated → null), never on the
     initial pre-auth render.

     Why this matters: the checkout-success/signup page is visited while still
     unauthenticated. main.tsx boots PostHog as the marketing-site person via
     bootstrap.distinctID (from ph_distinct_id) so the post-checkout `purchase`
     event stitches to the pre-checkout funnel (lp_viewed → begin_checkout).
     Calling reset() on that pre-auth render wiped that bootstrapped id and
     minted a fresh anonymous id, so `purchase` landed on a different person —
     breaking the begin_checkout → purchase funnel and starving Meta of
     conversion attribution. Resetting only on a genuine logout preserves the
     stitch. */
  const wasAuthenticated = useRef(false);

  /* Bumped by refreshPostHogIdentity() so the effect below re-reads profile +
     subscription without needing the `user` object itself to change. */
  const [refreshNonce, setRefreshNonce] = useState(0);

  const handleRefresh = useCallback(() => setRefreshNonce((n) => n + 1), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.addEventListener(IDENTITY_REFRESH_EVENT, handleRefresh);
    return () => window.removeEventListener(IDENTITY_REFRESH_EVENT, handleRefresh);
  }, [handleRefresh]);

  useEffect(() => {
    if (!user) {
      // Only reset on a genuine logout, not the initial unauthenticated load.
      if (wasAuthenticated.current) {
        resetUser();
        wasAuthenticated.current = false;
      }
      return;
    }

    wasAuthenticated.current = true;

    // Meta Advanced Matching — attach hashed email + user id to all
    // subsequent pixel events this session. initPixel() ran pre-auth, so
    // without this every portal event fires unidentified (PageView EMQ
    // was 6.1/10 with em at 0% — 2026-07-06). Fire-and-forget.
    void setPixelAdvancedMatching({ email: user.email, externalId: user.id });

    // ⚠️ 2026-08-08: merge the EMAIL-keyed person into this UUID-keyed one.
    // apps/server captures with the user's email as distinct_id
    // (posthogCapture.ts, checkoutLink.ts, resendWebhook.ts) while this app
    // identifies on the Supabase UUID, and nothing ever joined the two: in a
    // 90-day check all 559 email_delivered/opened/clicked events sat on
    // email-keyed people with zero overlap with purchase / bill_submitted, so
    // "email_clicked → purchase" was not a buildable funnel. aliasIdentity()
    // is guarded, so this is a no-op on every render after the first.
    if (user.email) {
      aliasIdentity(user.email.trim().toLowerCase());
    }

    (async () => {
      try {
        const [profileRes, subRes] = await Promise.all([
          supabase
            .from('profiles')
            .select(
              'first_name, last_name, referral_source, gender, state, created_at, ' +
                'utm_source, utm_medium, utm_campaign, utm_content, utm_term, ' +
                'initial_referrer, landing_page, acquisition_channel, is_test'
            )
            .eq('id', user.id)
            .maybeSingle(),
          supabase
            .from('subscriptions')
            .select('plan, status, bills_used, bills_limit')
            .eq('user_id', user.id)
            .maybeSingle(),
        ]);

        const profile = profileRes.data as ProfileRow | null;
        const sub = subRes.data as SubscriptionRow | null;

        identifyUser(user.id, {
          // Only ever set to true. Undefined is stripped, so a real user's
          // person never gets the flag at all.
          $internal_or_test_user: profile?.is_test ? true : undefined,
          email: user.email,
          name: [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || undefined,
          plan: sub?.plan || undefined,
          subscription_status: sub?.status || undefined,
          bills_used: sub?.bills_used ?? undefined,
          bills_limit: sub?.bills_limit ?? undefined,
          referral_source: profile?.referral_source || undefined,
          gender: profile?.gender || undefined,
          state: profile?.state || undefined,
          created_at: profile?.created_at || undefined,
          // First-touch UTM attribution, see migration 043. These are
          // person-level properties so PostHog filters like
          // "show me funnels for users where utm_source = fb" work
          // without needing to join against per-event properties.
          utm_source: profile?.utm_source || undefined,
          utm_medium: profile?.utm_medium || undefined,
          utm_campaign: profile?.utm_campaign || undefined,
          utm_content: profile?.utm_content || undefined,
          utm_term: profile?.utm_term || undefined,
          initial_referrer: profile?.initial_referrer || undefined,
          landing_page: profile?.landing_page || undefined,
          acquisition_channel: profile?.acquisition_channel || undefined,
        });
      } catch (err) {
        console.error('[PostHog] Failed to identify user:', err);
      }
    })();
  }, [user, refreshNonce]);

  return <>{children}</>;
}
